import { isNullish } from '@/utils/parsing'

export function areSame<T>(a: T, b: T) {
  // ignore special types (functions, methods...)
  if (
    typeof a === 'function' ||
    typeof a === 'symbol' ||
    typeof b === 'function' ||
    typeof b === 'symbol'
  ) {
    return true
  }

  // if one is Nullish (null or undefined), both should be
  if (isNullish(a) || isNullish(b)) {
    return isNullish(a) && isNullish(b)
  }

  // compare types
  if (typeof a !== typeof b) {
    return false
  }

  // compare strings, numbers and booleans
  if (
    typeof a === 'string' ||
    typeof a === 'number' ||
    typeof a === 'boolean'
  ) {
    return a === b
  }

  // compare arrays
  if (Array.isArray(a) || Array.isArray(b)) {
    // both should be arrays
    if (!Array.isArray(a) || !Array.isArray(b)) {
      return false
    }

    // both should have the same length
    if (a.length !== b.length) {
      return false
    }

    // compare each element
    for (let i = 0; i < a.length; i++) {
      if (!areSame(a[i], b[i])) {
        return false
      }
    }

    return true
  }

  // compare objects

  // get all unique keys
  const allKeys = [
    ...new Set(
      [a, b].flatMap((object) => Object.keys(object as Record<string, any>))
    ),
  ]

  // compare each field
  for (const key of allKeys) {
    if (!areSame((a as any)[key], (b as any)[key])) {
      return false
    }
  }

  return true
}

export function getDifference<T extends Record<string, any>>(
  previous: T,
  next: T
) {
  // get all unique keys
  const allKeys = [
    ...new Set(
      [previous, next].flatMap((object) =>
        Object.keys(object as Record<string, any>)
      )
    ),
  ]

  // calculate difference
  const difference = allKeys.reduce((result, key) => {
    // if fields differ, add to result
    if (!areSame(previous[key], next[key])) {
      result[key as keyof Partial<T>] = next[key]
    }

    return result
  }, {} as Partial<T>)

  return difference
}

export function mergeWithDifference<T extends Record<string, any>>({
  previous,
  next,
  mergeTarget,
}: {
  previous: T
  next: T
  mergeTarget: T
}) {
  const difference = getDifference(previous, next)

  return {
    ...mergeTarget,
    ...difference,
  }
}
